
import { Vue, Options } from "vue-class-component";
import { Watch, Ref } from "vue-property-decorator";
import { RouteLocation } from "vue-router";
import DropdownButton from "../atoms/DropdownButton.vue";
import Avatar from "../atoms/Avatar.vue";
import { toDepartmentPath, toClientPath } from "../../helpers/path-utils";
import {
  AuthApi,
  RoleName,
  SystemApi,
  GetPricePlan,
  PricePlanEnum,
  NavNode,
  Child,
  EmployeeApi,
  UpdateAvatar,
  NotificationsApi,
  NotificationLanguageEnum,
  NotificationContextEnum,
  Notification
} from "../../autogen";
import Store from "../../store";
import ChangePassword from "../organisms/ChangePassword.vue";
import UIkit from "uikit";
import * as RoleHelper from "../../helpers/role-helper";
import NavBarRow from "../molecules/NavBarRow.vue";
import { InstructionLevel, navMenu } from "../../types";

const ClientRelatedPaths = ["/analysis", "/as", "/organization", "/applicants", "/advance", "/users"];

declare module "@vue/runtime-core" {
  export interface ComponentCustomProperties {
    $role: typeof RoleHelper;
  }
}

@Options({
  components: { DropdownButton, Avatar, ChangePassword, NavBarRow }
})
export default class Menu extends Vue {
  private readonly storeInstance = Store.getInstance();
  departmentId = "all";
  activePath = "dashboard";
  formKey = 0;
  @Ref("modal") modal!: HTMLElement;
  @Ref("dropdown") dropdown!: HTMLElement;
  isTrial = true;
  priceResponse: GetPricePlan[] = [];
  planName = "";
  availableAdminFunc = RoleName.ClientAdmin;
  availableDepOwnerFunc = RoleName.DepartmentOwner;
  @Ref("avatarField") avatarField!: HTMLElement;
  imageMaxWidth = 200;
  acceptImageType = ["image/png", "image/jpeg"];
  avatarImage?: string | null = this.storeInstance.getAuth()?.user.image;
  @Ref("notificationsDrop") notificationsDrop!: HTMLElement;
  notifications: Notification[] = [];
  unreadNotificationsCount: number = 0;
  navMenu: navMenu[] = [];
  defalutNavMenu: navMenu[] = [
    {
      id: "dashboard",
      name: "ダッシュボード",
      path: `/clients/${this.clientId}/departments/${this.departmentId}/dashboard`,
      child: []
    },
    {
      id: "employees",
      name: "従業員<br />データベース",
      path: `/clients/${this.clientId}/departments/${this.departmentId}/employees`,
      child: []
    },
    {
      id: "applicants",
      name: "応募者<br />データベース",
      path: `/clients/${this.clientId}/applicants`,
      child: [
        { name: "全て", path: `/clients/${this.clientId}/applicants` },
        {
          name: "選考中",
          path: `/clients/${this.clientId}/applicants/?status=screening`
        },
        {
          name: "採用",
          path: `/clients/${this.clientId}/applicants/?status=hired`
        },
        {
          name: "不採用",
          path: `/clients/${this.clientId}/applicants/?status=not_hired`
        },
        {
          name: "入社",
          path: `/clients/${this.clientId}/applicants/?status=joined`
        }
      ]
    },
    {
      id: "analysis",
      name: "分析",
      path: `/clients/${this.clientId}/analysis`,
      child: [
        { name: "RD分析", path: `/clients/${this.clientId}/analysis/rd` },
        { name: "PD分析", path: `/clients/${this.clientId}/analysis/pd` },
        { name: "PM分析", path: `/clients/${this.clientId}/analysis/pm` },
        { name: "HS分析", path: `/clients/${this.clientId}/analysis/hs` },
        { name: "性格マトリクス", path: `/clients/${this.clientId}/analysis/personalityMatrix` },
        { name: "経年データ", path: `/clients/${this.clientId}/analysis/progress` }
      ]
    },
    {
      id: "as",
      name: "アセスメント<br />サーベイ",
      path: `/clients/${this.clientId}/as`
    },
    {
      id: "organization",
      name: "管理",
      path: `/clients/${this.clientId}/organization`,
      child: [
        { name: "部署管理", path: `/clients/${this.clientId}/organization/departments` },
        { name: "権限管理", path: `/clients/${this.clientId}/organization/authority` },
        { name: "役職管理", path: `/clients/${this.clientId}/organization/positions` },
        { name: "受検画面管理", path: `/clients/${this.clientId}/organization/exam` },
        { name: "メールテンプレート", path: `/clients/${this.clientId}/organization/mail_templates` },
        { name: "アクセス履歴", path: `/clients/${this.clientId}/organization/access_logs` },
        { name: "バックグラウンド処理状況", path: `/clients/${this.clientId}/organization/queue_manages` }
      ]
    },
    {
      id: "advance",
      name: "特別管理",
      path: `/clients/${this.clientId}/advance`,
      child: [
        { name: "タグ管理", path: `/clients/${this.clientId}/advance/tags` },
        { name: "人材基準管理", path: `/clients/${this.clientId}/advance/hr_criteria` },
        { name: "退職者一覧", path: `/clients/${this.clientId}/advance/retired` }
      ]
    },
    {
      id: "system",
      name: "システム設定",
      path: `/system`
    }
  ];
  get fullName() {
    const user = this.storeInstance.getAuth()?.user;
    return user ? `${user.lastName} ${user.firstName}` : "";
  }
  get isClientAdmin() {
    const role = this.storeInstance.getRole();
    return role && role.id === RoleName.ClientAdmin;
  }
  get roleName() {
    const role = this.storeInstance.getRole();
    return role ? role.name : "";
  }
  get clientId() {
    const auth = this.storeInstance.getAuth();
    return auth ? auth.clientId : "0";
  }
  get planEnd() {
    const auth = this.storeInstance.getAuth();
    if (auth && auth.pricePlan !== PricePlanEnum.Trial) {
      const date = auth.planEnd && new Date(auth.planEnd as string);
      return date ? `${date.getFullYear()}年${date.getMonth() + 1}月` : "";
    }
    return "";
  }
  get isDeleteScheduled() {
    const auth = this.storeInstance.getAuth();
    return auth && auth.isDeleteScheduled;
  }

  private belongsToClient(path: string) {
    return ClientRelatedPaths.some(p => path.indexOf(p) >= 0);
  }
  private setActive(to: string) {
    if (to === "/system") {
      this.activePath = "system";
    } else if (this.belongsToClient(to)) {
      const path = toClientPath(to);
      this.activePath = path.activePath.split("/")[0];
    } else {
      const path = toDepartmentPath(to);
      this.departmentId = path.departmentId;
      this.activePath = path.activePath;
    }
  }

  private async registerEvents() {
    this.notificationsDrop.addEventListener("shown", async () => {
      const unreadNotificationIds = this.notifications.filter(({ isRead }) => !isRead).map(({ id }) => id);
      if (unreadNotificationIds.length) {
        await new NotificationsApi().createNotificationReceipt({
          postNotificationReceipt: {
            notificationIds: unreadNotificationIds
          }
        });
      }
      this.unreadNotificationsCount = 0;
    });

    this.notificationsDrop.addEventListener("hidden", async () => {
      await this.loadNotifications();
    });
  }

  private async loadNotifications() {
    try {
      const response = await new NotificationsApi().getNotifications({
        language: NotificationLanguageEnum.jp,
        context: NotificationContextEnum.mgtScreen
      });
      this.notifications = response.data;
      this.unreadNotificationsCount = response.data.filter(({ isRead }) => !isRead).length;
    } catch (err) {
      console.log(err);
    }
  }
  @Watch("$route")
  onRouteChanged(to: RouteLocation) {
    this.setActive(to.path);
    this.navMenu = this.$role.setNavByRole(this.defalutNavMenu, this.isTrial)!;
    this.setDepartmentsOnNavi();
  }
  async beforeMount() {
    this.setActive(this.$route.path);
    const planCode = this.storeInstance.getAuth()!.pricePlan;
    this.isTrial = planCode === PricePlanEnum.Trial;
    this.navMenu = this.$role.setNavByRole(this.defalutNavMenu, this.isTrial)!;
    this.setDepartmentsOnNavi();
    if (!this.priceResponse.length) {
      const response = await new SystemApi().getPricePlans({});
      this.priceResponse = response.data;
    }
    if (this.priceResponse.length && this.$role.isStrongerThan(this.availableAdminFunc)) {
      const plan = Object.entries(this.priceResponse).find(([_, e]) => e.code === planCode);
      this.planName = plan ? plan[1].name : "";
    }

    await this.registerEvents();
    await this.loadNotifications();
  }
  @Watch("storeInstance.state.departments")
  stateDepartmentsChange() {
    this.setDepartmentsOnNavi();
  }
  @Watch("storeInstance.state.role.permissions")
  stateRoleChange() {
    this.navMenu = this.$role.setNavByRole(this.defalutNavMenu, this.isTrial)!;
    this.setDepartmentsOnNavi();
  }
  /**
   * ダッシュボード、従業員管理の部署階層セット
   */
  setDepartmentsOnNavi() {
    const deps = this.storeInstance.getDepartments({});
    this.navMenu.map(r => {
      if (r.id && ["employees", "dashboard"].includes(r.id)) {
        r.child = this.getdepartmentChildPath(deps, r.id);
      }
      return r;
    });
  }
  /**
   * get department child path
   * @param {Child[]} child 部署ノード
   * @return child path付きノード
   */
  getdepartmentChildPath(child: Child[], uri: string) {
    return child.map((r: Child) => {
      const menu = Object.assign({}, r, {
        path: `/clients/${this.clientId}/departments/${r.id}/${uri}`
      });
      if (r.child && r.child.length) {
        menu.child = this.getdepartmentChildPath(r.child, uri);
      }
      return menu;
    });
  }
  /**
   * get role child path
   * @param {navNode[]} navメニュー
   * @return navNode[] ログインユーザーが利用可能なナビメニュー
   */
  getRoleAvailable(navNode: NavNode[]) {
    // TODO create logic at role
    return navNode;
  }
  async logout() {
    await new AuthApi().logout({});
    Store.getInstance().setAuth(null);
    this.$router.push("/login");
  }
  changePassword() {
    this.formKey = Date.now();
    UIkit.modal(this.modal).show();
  }
  onSubmitted() {
    UIkit.modal(this.modal).hide();
  }
  onClose() {
    UIkit.modal(this.modal).hide();
  }
  onAvatarLinkClick() {
    this.avatarField.click();
  }
  async onAvatarSelect() {
    UIkit.dropdown(this.dropdown).hide();
    if (!this.storeInstance.getAuth()!.user.employeeId) return;
    const target = this.avatarField as HTMLInputElement;
    const file = target.files![0];
    const imageType = file.type;
    if (imageType && !this.acceptImageType.includes(imageType)) {
      this.storeInstance.setInstructionContent(InstructionLevel.Alert, "顔写真はPNG、JPEGのみアップロード可能です");
      return false;
    }
    target.value = "";

    const resizeFunc = async () => {
      return new Promise((resolve, reject) => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const img = new Image();
        const fr = new FileReader();
        fr.onload = e => {
          img.src = e.target!.result as string;
          e.preventDefault();
          img.onload = () => {
            let [width, height] = [img.width, img.height];
            if (img.width > this.imageMaxWidth) {
              width = this.imageMaxWidth;
              height = Math.round(height * (width / img.width));
            }
            [canvas.width, canvas.height] = [width, height];
            ctx!.drawImage(img, 0, 0, canvas.width, canvas.height);
            this.avatarImage = ctx!.canvas.toDataURL() as string;
            resolve(img);
          };
        };
        fr.readAsDataURL(file);
      });
    };
    try {
      await resizeFunc();
      await new EmployeeApi().updateAvatar({
        updateAvatar: { image: this.avatarImage } as UpdateAvatar,
        employeeId: this.storeInstance.getAuth()!.user.employeeId!
      });
    } catch (err) {
      let errMessage = "エラーが発生しました";
      if (err.response && err.response.data) {
        errMessage = err.response.data.detail;
      }
      this.storeInstance.setInstructionContent(InstructionLevel.Alert, errMessage);
    }
  }
}
